if (!customElements.get('wishlist-drawer')) {
  customElements.define('wishlist-drawer', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.list = this.querySelector('[data-wishlist-list]');
      this.status = this.querySelector('[data-wishlist-status]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.retry = this.querySelector('[data-wishlist-retry]');
      this.events = new AbortController();
      const options = { signal: this.events.signal };

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-wishlist-open]');
        if (!trigger || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(trigger);
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-wishlist-close]')) this.dialog.close();
        else if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
        } else if (event.target.closest('[data-wishlist-remove]')) {
          this.removeProduct(event.target.closest('[data-wishlist-product]'));
        } else if (event.target.closest('[data-wishlist-add]')) {
          this.addToCart(event.target.closest('[data-wishlist-add]'));
        } else if (event.target.closest('[data-wishlist-retry]')) this.loadProducts();
      }, options);
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open) this.cleanup(true);
      }, options);
      window.addEventListener('storage', (event) => {
        if (this.dialog.open && (event.key === 'product_wishlist' || event.key === null)) this.loadProducts();
      }, options);
    }

    readIds() {
      const stored = JSON.parse(localStorage.getItem('product_wishlist') || '[]');
      if (!Array.isArray(stored)) throw new Error('Invalid wishlist data');
      return [...new Set(stored.map(String).filter(id => /^\d+$/.test(id)))];
    }

    open(trigger) {
      if (this.dialog.open) return;
      this.trigger = trigger;
      this.previousPadding = document.body.style.paddingRight;
      const gap = window.innerWidth - document.documentElement.clientWidth;
      const stableGutter = getComputedStyle(document.documentElement).scrollbarGutter?.includes('stable');
      if (gap > 0 && !stableGutter) {
        document.body.style.paddingRight = `${parseFloat(getComputedStyle(document.body).paddingRight) + gap}px`;
      }
      document.body.classList.add('wishlist-drawer-open');
      try {
        this.dialog.showModal();
      } catch (error) {
        this.cleanup(false);
        window.location.assign(trigger.href);
        return;
      }
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', this.dialog.id);
      trigger.setAttribute('aria-expanded', 'true');
      this.dialog.scrollTop = 0;
      this.loadProducts();
    }

    cleanup(restoreFocus) {
      this.request?.abort();
      const toast = this.cartMessage?.$elem;
      if (toast?.isConnected) {
        try {
          if (toast.matches(':popover-open')) toast.hidePopover();
        } catch (error) {
          // Cleanup must not prevent the drawer from restoring page scrolling.
        }
        toast.removeAttribute('popover');
        toast.classList.remove('wishlist-drawer-toast');
      }
      this.cartMessage = null;
      document.body.classList.remove('wishlist-drawer-open');
      if (this.previousPadding !== undefined) {
        document.body.style.paddingRight = this.previousPadding;
        this.previousPadding = undefined;
      }
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (restoreFocus && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
    }

    async loadProducts() {
      this.request?.abort();
      const request = this.request = new AbortController();
      this.list.replaceChildren();
      this.empty.hidden = true;
      this.retry.hidden = true;
      this.status.textContent = 'Loading your wishlist…';
      this.list.setAttribute('aria-busy', 'true');
      try {
        const ids = this.readIds();
        if (!ids.length) {
          this.empty.hidden = false;
          this.status.textContent = '';
          return;
        }
        const cards = new Map();
        const requestedIds = new Set(ids);
        const url = new URL(this.dataset.searchUrl, window.location.origin);
        url.searchParams.set('section_id', 'wishlist-drawer-products');
        url.searchParams.set('type', 'product');
        url.searchParams.set('q', ids.map(id => `id:${id}`).join(' OR '));
        // Match the wishlist page's full search query and default result order.
        // Paginate that result set instead of sorting separate ID batches locally.
        let pages = 1;
        for (let page = 1; page <= pages; page++) {
          url.searchParams.set('page', String(page));
          const response = await fetch(url, { signal: request.signal });
          if (!response.ok) throw new Error('Wishlist request failed');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!html.getElementById('shopify-section-wishlist-drawer-products')) throw new Error('Missing wishlist section');
          pages = Number(html.querySelector('[data-wishlist-pages]')?.dataset.wishlistPages || 1);
          if (!Number.isInteger(pages) || pages < 1 || pages > Math.ceil(ids.length / 50)) throw new Error('Invalid wishlist pagination');
          html.querySelectorAll('[data-wishlist-product]').forEach(card => {
            if (requestedIds.has(card.dataset.wishlistProduct)) cards.set(card.dataset.wishlistProduct, card);
          });
        }
        if (request.signal.aborted || !this.isConnected) return;
        this.list.replaceChildren(...cards.values());
        this.status.textContent = cards.size < ids.length ? 'Some saved items are no longer available.' : '';
      } catch (error) {
        if (request.signal.aborted) return;
        this.status.textContent = 'Unable to load your wishlist. Please try again.';
        this.retry.hidden = false;
      } finally {
        if (this.request === request) this.list.removeAttribute('aria-busy');
      }
    }

    removeProduct(card) {
      if (!card) return;
      try {
        const ids = this.readIds().filter(id => id !== card.dataset.wishlistProduct);
        localStorage.setItem('product_wishlist', JSON.stringify(ids));
        const nextFocus = card.nextElementSibling?.querySelector('[data-wishlist-remove]') || card.previousElementSibling?.querySelector('[data-wishlist-remove]') || this.querySelector('[data-wishlist-close]');
        card.remove();
        this.empty.hidden = ids.length > 0;
        this.status.textContent = ids.length && !this.list.children.length ? 'Some saved items are no longer available.' : '';
        nextFocus?.focus({ preventScroll: true });
        document.querySelectorAll('add-favorites').forEach(element => element.updateLikedState?.());
        // Refresh the original wishlist page too, if it is currently displayed.
        document.querySelector('wishlist-section')?.init?.();
        this.showCartMessage('success', 'Removed from wishlist.');
      } catch (error) {
        this.showCartMessage('error', 'Unable to update your wishlist. Please try again.');
      }
    }

    showCartMessage(type, text) {
      try {
        // Qmsg de-duplicates by its options. Keep drawer messages separate from
        // identical messages created elsewhere, without changing global defaults.
        const message = window.Qmsg?.[type]?.(text, { source: 'wishlist-drawer' });
        if (!message) {
          this.status.textContent = text;
          return;
        }
        if (!this.dialog.open) return;
        if (this.cartMessage && this.cartMessage !== message) this.cartMessage.destroy();
        this.cartMessage = message;
        const toast = message.$elem;
        if (!toast || typeof toast.showPopover !== 'function') {
          message.destroy();
          this.cartMessage = null;
          this.status.textContent = text;
          return;
        }
        // Promote only this message. Its DOM parent and the shared Qmsg wrapper
        // remain untouched; Qmsg still owns the animation and automatic dismissal.
        toast.classList.add('wishlist-drawer-toast');
        toast.setAttribute('popover', 'manual');
        toast.showPopover();
      } catch (error) {
        this.status.textContent = text;
      }
    }

    async addToCart(button) {
      if (button.disabled) return;
      const label = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';
      this.status.textContent = '';
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: button.dataset.wishlistAdd, quantity: 1 }], sections: 'cart-icon-bubble', sections_url: window.location.pathname })
        });
        if (!response.ok) throw new Error('Add to cart failed');
        const cartData = await response.json();
        if (!cartData || cartData.status) throw new Error('Add to cart failed');
        this.showCartMessage('success', 'Add to cart success');
        // Keep the wishlist open; the cart drawer refreshes its contents when opened.
        const icon = document.getElementById('cart-icon-bubble');
        if (icon && cartData.sections?.['cart-icon-bubble']) {
          const html = new DOMParser().parseFromString(cartData.sections['cart-icon-bubble'], 'text/html');
          const section = html.querySelector('.shopify-section');
          if (section) icon.replaceChildren(...section.childNodes);
        }
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wishlist-drawer', cartData });
          }
        } catch (error) {
          // The add succeeded; a subscriber error must not encourage a duplicate add.
          console.warn('Unable to notify cart subscribers:', error);
        }
      } catch (error) {
        console.warn('Wishlist add to cart failed:', error);
        this.showCartMessage('error', 'Add to cart failed');
      } finally {
        button.disabled = false;
        button.textContent = label;
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      if (this.dialog?.open) this.dialog.close();
      this.cleanup(false);
    }
  });
}
